import { PaymentVerificationResponse } from '../types/purchase';

import axiosInstance from './axiosInstance';
import { billingAttribution } from './billingAttribution';

export interface CreateOrderResponse {
  order_id: string;
  payment_id: string;
  amount: number;
  currency: string;
  prompt_credits: number;
  task_count: number;
  key_id: string;
  payment_provider?: string;        // 'razorpay' | 'stripe'
  stripe_checkout_url?: string;     // Only for Stripe
}

interface CreateTopUpResponse {
    order_id: string;
    payment_id: string;
    amount: number;
    currency: string;
    key_id: string;
    prompt_credits: number;
    payment_provider?: string;        // 'razorpay' | 'stripe'
    stripe_checkout_url?: string;     // Only for Stripe
}

export interface PaymentHistoryItem {
  id: string;
  amount_cents: number;
  currency: string;
  prompt_credits: number;
  status: string;
  payment_provider?: string;
  razorpay_order_id: string | null;
  razorpay_payment_id: string | null;
  created_at: string | null;
  completed_at: string | null;
}

export interface OutstandingBalance {
  amount_cents: number;
  amount_usd: number;
  prompt_credits: number;
  task_count: number;
}

function extractFilename(
  contentDisposition: unknown,
  contentType: unknown,
  paymentId: string,
): string {
  if (typeof contentDisposition === 'string') {
    const match = contentDisposition.match(/filename=(.+)/);

    if (match) return match[1];
  }

  const isPdf = typeof contentType === 'string' && contentType.includes('application/pdf');
  const ext = isPdf ? 'pdf' : 'txt';

  return `receipt-${paymentId.slice(0, 8)}.${ext}`;
}

function createDownloadLink(url: string, filename: string): globalThis.HTMLAnchorElement {
  const a = document.createElement('a');

  a.href = url;

  a.download = filename;

  return a;
}

function clickAndCleanup(a: globalThis.HTMLAnchorElement, url: string): void {
  document.body.appendChild(a);

  a.click();

  document.body.removeChild(a);

  window.URL.revokeObjectURL(url);
}

function triggerDownload(blob: globalThis.Blob, filename: string): void {
  const url = window.URL.createObjectURL(new window.Blob([blob]));

  const a = createDownloadLink(url, filename);

  clickAndCleanup(a, url);
}

export const paymentService = {
  createOrder: async (taskIds?: string[]): Promise<CreateOrderResponse> => {
    const response = await axiosInstance.post('/api/v1/payments/create-order', {
      task_ids: taskIds,
    });

    return response.data;
  },

  createTopUpOrder: async (creditAmount: number, currency: string = 'USD'): Promise<CreateTopUpResponse> => {
    const journeyId = await billingAttribution.startCheckout({ intent: 'credit_topup', credits: creditAmount });
    const response = await axiosInstance.post('/api/v1/payments/create-topup-order', {
      credit_amount: creditAmount,
      currency: currency,
    });
    await billingAttribution.linkCheckout(journeyId, response.data);

    return response.data;
  },

  verifyPayment: async (
    orderId: string,
    paymentId: string,
    signature: string
  ): Promise<PaymentVerificationResponse & { payment_id: string }> => {
    const response = await axiosInstance.post('/api/v1/payments/verify', {
      order_id: orderId,
      payment_id: paymentId,
      signature: signature,
    });

    return response.data;
  },

  getHistory: async (limit: number = 50): Promise<PaymentHistoryItem[]> => {
    const response = await axiosInstance.get('/api/v1/payments/history', {
      params: { limit },
    });

    return response.data;
  },

  getOutstandingBalance: async (): Promise<OutstandingBalance> => {
    const response = await axiosInstance.get('/api/v1/payments/balance');

    return response.data;
  },

  downloadReceipt: async (paymentId: string): Promise<void> => {
    const response = await axiosInstance.get(`/api/v1/payments/${paymentId}/receipt/download`, {
      responseType: 'blob',
    });

    const filename = extractFilename(response.headers['content-disposition'], response.headers['content-type'], paymentId);

    triggerDownload(response.data, filename);
  },
};
